/**
 * Random-access zip reading over a Blob. The central directory is parsed once
 * (~100 bytes per entry); entry bytes are sliced and inflated on demand, so
 * the archive's payload never has to sit in the JS heap the way an eagerly
 * loaded ArrayBuffer does. Storage stays in the browser-managed Blob.
 */

const EOCD_SIGNATURE = 0x06054b50
const EOCD_MIN_BYTES = 22
const EOCD_MAX_COMMENT_BYTES = 0xffff
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50
const ZIP64_LOCATOR_BYTES = 20
const ZIP64_EOCD_SIGNATURE = 0x06064b50
const CENTRAL_HEADER_SIGNATURE = 0x02014b50
const LOCAL_HEADER_SIGNATURE = 0x04034b50
const ZIP64_EXTRA_FIELD_ID = 0x0001
const ZIP32_SENTINEL = 0xffffffff
const ZIP16_SENTINEL = 0xffff
const STORED_METHOD = 0
const DEFLATE_METHOD = 8
const STORED_STREAM_CHUNK_BYTES = 64 * 1024

export interface BlobArchiveEntry {
  compressedSize: number
  isDirectory: boolean
  name: string
  uncompressedSize: number
}

export interface BlobArchive {
  entries: readonly BlobArchiveEntry[]
  readBytes: (name: string) => Promise<Uint8Array>
  readStream: (name: string) => ReadableStream<Uint8Array>
  readString: (name: string) => Promise<string>
}

interface EntryRecord extends BlobArchiveEntry {
  localHeaderOffset: number
  method: number
}

const invalidArchive = (reason: string) =>
  new Error(`The Office archive is not a readable zip: ${reason}.`)

const readU64 = (view: DataView, offset: number) => {
  const value = view.getBigUint64(offset, true)
  if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw invalidArchive('a 64-bit field exceeds the addressable range')
  }
  return Number(value)
}

const findEndOfCentralDirectory = async (blob: Blob) => {
  const tailBytes = Math.min(blob.size, EOCD_MIN_BYTES + EOCD_MAX_COMMENT_BYTES)
  const tailStart = blob.size - tailBytes
  const tail = new DataView(await blob.slice(tailStart).arrayBuffer())
  const recordAt = (offset: number) => ({
    centralDirectoryOffset: tail.getUint32(offset + 16, true),
    centralDirectorySize: tail.getUint32(offset + 12, true),
    endOfCentralDirectoryOffset: tailStart + offset,
    totalEntries: tail.getUint16(offset + 10, true),
  })
  // A trailer whose comment ends exactly at the source's last byte is
  // authoritative: it cannot be a false signature inside a comment, because
  // random comment bytes do not also declare the right comment length. A
  // trailer with bytes after it is only trusted when no exact trailer
  // exists, which tolerates archives padded by transfer tooling without
  // opening well-formed commented archives at the wrong record.
  let paddedRecord: ReturnType<typeof recordAt> | null = null
  for (let offset = tail.byteLength - EOCD_MIN_BYTES; offset >= 0; offset--) {
    if (tail.getUint32(offset, true) !== EOCD_SIGNATURE) {
      continue
    }
    const commentEnd =
      offset + EOCD_MIN_BYTES + tail.getUint16(offset + 20, true)
    if (commentEnd === tail.byteLength) {
      return recordAt(offset)
    }
    if (commentEnd < tail.byteLength) {
      paddedRecord ??= recordAt(offset)
    }
  }
  if (paddedRecord) {
    return paddedRecord
  }
  throw invalidArchive('the end-of-central-directory record is missing')
}

const applyZip64 = async (
  blob: Blob,
  record: Awaited<ReturnType<typeof findEndOfCentralDirectory>>,
) => {
  const needsZip64 =
    record.totalEntries === ZIP16_SENTINEL ||
    record.centralDirectorySize === ZIP32_SENTINEL ||
    record.centralDirectoryOffset === ZIP32_SENTINEL
  if (!needsZip64) {
    return { ...record, isZip64: false }
  }
  const locatorOffset = record.endOfCentralDirectoryOffset - ZIP64_LOCATOR_BYTES
  if (locatorOffset < 0) {
    throw invalidArchive('the zip64 locator is missing')
  }
  const locator = new DataView(
    await blob
      .slice(locatorOffset, locatorOffset + ZIP64_LOCATOR_BYTES)
      .arrayBuffer(),
  )
  if (locator.getUint32(0, true) !== ZIP64_LOCATOR_SIGNATURE) {
    throw invalidArchive('the zip64 locator is malformed')
  }
  const zip64Offset = readU64(locator, 8)
  const zip64 = new DataView(
    await blob.slice(zip64Offset, zip64Offset + 56).arrayBuffer(),
  )
  if (
    zip64.byteLength < 56 ||
    zip64.getUint32(0, true) !== ZIP64_EOCD_SIGNATURE
  ) {
    throw invalidArchive(
      'the zip64 end-of-central-directory record is malformed',
    )
  }
  return {
    ...record,
    centralDirectoryOffset: readU64(zip64, 48),
    centralDirectorySize: readU64(zip64, 40),
    isZip64: true,
    totalEntries: readU64(zip64, 32),
  }
}

const zip64ExtraOverrides = (
  view: DataView,
  extraStart: number,
  extraLength: number,
  record: {
    compressedSize: number
    localHeaderOffset: number
    uncompressedSize: number
  },
) => {
  let offset = extraStart
  const extraEnd = extraStart + extraLength
  while (offset + 4 <= extraEnd) {
    const fieldId = view.getUint16(offset, true)
    const fieldSize = view.getUint16(offset + 2, true)
    const fieldStart = offset + 4
    if (fieldStart + fieldSize > extraEnd) {
      break
    }
    if (fieldId === ZIP64_EXTRA_FIELD_ID) {
      // Fields appear in a fixed order, but only the ones whose 32-bit
      // counterpart carries the sentinel.
      let fieldOffset = fieldStart
      const takeU64 = () => {
        if (fieldOffset + 8 > fieldStart + fieldSize) {
          throw invalidArchive('a zip64 extra field is truncated')
        }
        const value = readU64(view, fieldOffset)
        fieldOffset += 8
        return value
      }
      if (record.uncompressedSize === ZIP32_SENTINEL) {
        record.uncompressedSize = takeU64()
      }
      if (record.compressedSize === ZIP32_SENTINEL) {
        record.compressedSize = takeU64()
      }
      if (record.localHeaderOffset === ZIP32_SENTINEL) {
        record.localHeaderOffset = takeU64()
      }
      return
    }
    offset = fieldStart + fieldSize
  }
}

const parseCentralDirectory = (
  directory: DataView,
  totalEntries: number,
): Map<string, EntryRecord> => {
  const decoder = new TextDecoder('utf-8')
  const bytes = new Uint8Array(
    directory.buffer,
    directory.byteOffset,
    directory.byteLength,
  )
  const records = new Map<string, EntryRecord>()
  let offset = 0
  for (let index = 0; index < totalEntries; index++) {
    if (
      offset + 46 > directory.byteLength ||
      directory.getUint32(offset, true) !== CENTRAL_HEADER_SIGNATURE
    ) {
      throw invalidArchive('the central directory is truncated')
    }
    const nameLength = directory.getUint16(offset + 28, true)
    const extraLength = directory.getUint16(offset + 30, true)
    const commentLength = directory.getUint16(offset + 32, true)
    const recordEnd = offset + 46 + nameLength + extraLength + commentLength
    if (recordEnd > directory.byteLength) {
      throw invalidArchive('a central directory record is truncated')
    }
    // OPC part names are ASCII by spec; anything beyond that must be
    // UTF-8-flagged, and consumers reject undecodable part names anyway.
    // Some Windows zip tools store backslash separators in the central
    // directory (JSZip sidestepped this by taking names from the local
    // headers, which those tools write correctly).
    const name = decoder
      .decode(bytes.subarray(offset + 46, offset + 46 + nameLength))
      .replaceAll('\\', '/')
    const record: EntryRecord = {
      compressedSize: directory.getUint32(offset + 20, true),
      isDirectory: name.endsWith('/'),
      localHeaderOffset: directory.getUint32(offset + 42, true),
      method: directory.getUint16(offset + 10, true),
      name,
      uncompressedSize: directory.getUint32(offset + 24, true),
    }
    zip64ExtraOverrides(
      directory,
      offset + 46 + nameLength,
      extraLength,
      record,
    )
    records.set(name, record)
    offset = recordEnd
  }
  return records
}

const isArchiveError = (error: unknown) =>
  error instanceof Error &&
  error.message.startsWith('The Office archive is not a readable zip')

const inflateRaw = async (compressed: Blob, declaredSize: number) => {
  const output = new Uint8Array(declaredSize)
  // Some producers write a deflate entry with no payload at all for an
  // empty file; DecompressionStream rejects zero input outright while zip
  // readers conventionally treat it as empty.
  if (compressed.size === 0) {
    if (declaredSize !== 0) {
      throw invalidArchive('an entry inflates short of its declared size')
    }
    return output
  }
  const reader = compressed
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'))
    .getReader()
  let written = 0
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) {
        break
      }
      if (written + value.byteLength > declaredSize) {
        throw invalidArchive('an entry inflates beyond its declared size')
      }
      output.set(value, written)
      written += value.byteLength
    }
  } catch (error) {
    // The failure paths must tear the pipeline down; releaseLock alone
    // leaves the decompression stream live. Cancelling an already-errored
    // stream rejects with its stored error, hence the swallow.
    await reader.cancel(error).catch(() => {})
    if (isArchiveError(error)) {
      throw error
    }
    // DecompressionStream reports corrupt input as a bare TypeError.
    throw invalidArchive('an entry fails to inflate')
  } finally {
    reader.releaseLock()
  }
  if (written !== declaredSize) {
    throw invalidArchive('an entry inflates short of its declared size')
  }
  return output
}

export const openBlobArchive = async (blob: Blob): Promise<BlobArchive> => {
  if (blob.size < EOCD_MIN_BYTES) {
    throw invalidArchive('the source is smaller than a zip trailer')
  }
  const endRecord = await applyZip64(
    blob,
    await findEndOfCentralDirectory(blob),
  )
  // Archives with data prepended ahead of the first local header store
  // offsets relative to the archive start, not the blob start. The delta is
  // the gap between where the stored offsets say the central directory ends
  // and where the trailer actually sits (JSZip's `extraBytes` rebase). The
  // zip64 trailer's own position is only discoverable through those same
  // stored offsets, so prepended zip64 archives stay unsupported.
  const offsetRebase = endRecord.isZip64
    ? 0
    : endRecord.endOfCentralDirectoryOffset -
      (endRecord.centralDirectoryOffset + endRecord.centralDirectorySize)
  if (offsetRebase < 0) {
    throw invalidArchive('the central directory overlaps the trailer')
  }
  const centralDirectoryStart = endRecord.centralDirectoryOffset + offsetRebase
  if (centralDirectoryStart + endRecord.centralDirectorySize > blob.size) {
    throw invalidArchive('the central directory lies outside the source')
  }
  const directory = new DataView(
    await blob
      .slice(
        centralDirectoryStart,
        centralDirectoryStart + endRecord.centralDirectorySize,
      )
      .arrayBuffer(),
  )
  const records = parseCentralDirectory(directory, endRecord.totalEntries)

  const resolveCompressedSlice = async (name: string, record: EntryRecord) => {
    // The local header's name/extra lengths can differ from the central
    // directory's, so the data offset must be derived from the local copy.
    const localHeaderOffset = record.localHeaderOffset + offsetRebase
    const localHeader = new DataView(
      await blob.slice(localHeaderOffset, localHeaderOffset + 30).arrayBuffer(),
    )
    if (
      localHeader.byteLength < 30 ||
      localHeader.getUint32(0, true) !== LOCAL_HEADER_SIGNATURE
    ) {
      throw invalidArchive(`the entry ${name} has a malformed local header`)
    }
    const dataStart =
      localHeaderOffset +
      30 +
      localHeader.getUint16(26, true) +
      localHeader.getUint16(28, true)
    const compressed = blob.slice(dataStart, dataStart + record.compressedSize)
    if (compressed.size !== record.compressedSize) {
      throw invalidArchive(`the entry ${name} is truncated`)
    }
    if (
      record.method === STORED_METHOD &&
      record.compressedSize !== record.uncompressedSize
    ) {
      throw invalidArchive(`the entry ${name} has inconsistent stored sizes`)
    }
    if (record.method !== STORED_METHOD && record.method !== DEFLATE_METHOD) {
      throw invalidArchive(
        `the entry ${name} uses an unsupported compression method`,
      )
    }
    return compressed
  }

  const readBytes = async (name: string) => {
    const record = records.get(name)
    if (!record) {
      throw invalidArchive(`the entry ${name} does not exist`)
    }
    const compressed = await resolveCompressedSlice(name, record)
    if (record.method === STORED_METHOD) {
      return new Uint8Array(await compressed.arrayBuffer())
    }
    return inflateRaw(compressed, record.uncompressedSize)
  }

  const readStream = (name: string): ReadableStream<Uint8Array> => {
    const record = records.get(name)
    if (!record) {
      throw invalidArchive(`the entry ${name} does not exist`)
    }
    let compressed: Blob | null = null
    let inflateReader: ReadableStreamDefaultReader<Uint8Array> | null = null
    let offset = 0
    let produced = 0
    return new ReadableStream<Uint8Array>({
      pull: async (controller) => {
        compressed ??= await resolveCompressedSlice(name, record)
        if (record.method === STORED_METHOD) {
          // Blob.stream() may hand an in-memory blob over as one chunk;
          // bounded windows keep early-stopping consumers cheap.
          if (offset >= compressed.size) {
            controller.close()
            return
          }
          const end = Math.min(
            offset + STORED_STREAM_CHUNK_BYTES,
            compressed.size,
          )
          const value = new Uint8Array(
            await compressed.slice(offset, end).arrayBuffer(),
          )
          offset = end
          controller.enqueue(value)
          return
        }
        if (compressed.size === 0) {
          if (record.uncompressedSize !== 0) {
            throw invalidArchive('an entry inflates short of its declared size')
          }
          controller.close()
          return
        }
        inflateReader ??= compressed
          .stream()
          .pipeThrough(new DecompressionStream('deflate-raw'))
          .getReader()
        const { done, value } = await inflateReader.read().catch((error) => {
          if (isArchiveError(error)) {
            throw error
          }
          // DecompressionStream reports corrupt input as a bare TypeError.
          throw invalidArchive('an entry fails to inflate')
        })
        if (done) {
          if (produced !== record.uncompressedSize) {
            throw invalidArchive('an entry inflates short of its declared size')
          }
          controller.close()
          return
        }
        produced += value.byteLength
        if (produced > record.uncompressedSize) {
          // The cancel callback below only runs for consumer cancellation,
          // so the still-live pipeline must be torn down here.
          const error = invalidArchive(
            'an entry inflates beyond its declared size',
          )
          await inflateReader.cancel(error).catch(() => {})
          throw error
        }
        controller.enqueue(value)
      },
      cancel: async (reason) => {
        await inflateReader?.cancel(reason)
      },
    })
  }

  const utf8 = new TextDecoder('utf-8')
  return {
    entries: [...records.values()].map(
      ({ compressedSize, isDirectory, name, uncompressedSize }) => ({
        compressedSize,
        isDirectory,
        name,
        uncompressedSize,
      }),
    ),
    readBytes,
    readStream,
    readString: async (name) => utf8.decode(await readBytes(name)),
  }
}
